import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Copy, KeyRound, Pencil, Plus, Trash2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from "@nexus/ui";
import { hasActionPermission } from "@/features/auth/lib/has-action-permission";
import {
  createPublicApiToken,
  revokePublicApiToken,
  updatePublicApiToken,
} from "@/features/public-api/api/tokens.api";
import {
  publicApiTokensKeys,
  publicApiTokensListQueryOptions,
} from "@/lib/queries/public-api-tokens.queries";
import { toast } from "sonner";
import type { PublicApiTokenListItem } from "@nexus/contracts";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export function ApiTokensPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: tokens = [], isLoading } = useQuery(publicApiTokensListQueryOptions());

  const [createOpen, setCreateOpen] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [editToken, setEditToken] = useState<PublicApiTokenListItem | null>(null);
  const [revokeToken, setRevokeToken] = useState<PublicApiTokenListItem | null>(null);

  const canList = hasActionPermission("PUBLIC_API_LIST_TOKENS");
  const canCreate = hasActionPermission("PUBLIC_API_CREATE_TOKEN");
  const canUpdate = hasActionPermission("PUBLIC_API_UPDATE_TOKEN");
  const canDelete = hasActionPermission("PUBLIC_API_DELETE_TOKEN");

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: publicApiTokensKeys.all });

  const createMutation = useMutation({
    mutationFn: createPublicApiToken,
    onSuccess: (data) => {
      setCreatedSecret(data.token);
      invalidate();
      toast.success(t("publicApi.tokens.createSuccess"));
    },
    onError: () => toast.error(t("publicApi.tokens.createError")),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name, description }: { id: string; name: string; description: string }) =>
      updatePublicApiToken(id, { name, description: description || null }),
    onSuccess: () => {
      setEditToken(null);
      invalidate();
      toast.success(t("publicApi.tokens.updateSuccess"));
    },
    onError: () => toast.error(t("publicApi.tokens.updateError")),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokePublicApiToken(id),
    onSuccess: () => {
      setRevokeToken(null);
      invalidate();
      toast.success(t("publicApi.tokens.revokeSuccess"));
    },
    onError: () => toast.error(t("publicApi.tokens.revokeError")),
  });

  const copySecret = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("publicApi.tokens.copied"));
    } catch {
      toast.error(t("publicApi.tokens.copyError"));
    }
  };

  if (!canList) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-muted-foreground">
        {t("publicApi.tokens.noListPermission")}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t("publicApi.tokens.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("publicApi.tokens.subtitle")}</p>
        </div>
        {canCreate && (
          <Button onClick={() => { setCreateOpen(true); setCreatedSecret(null); }}>
            <Plus className="mr-2 h-4 w-4" />
            {t("publicApi.tokens.create")}
          </Button>
        )}
      </div>

      <div className="overflow-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("publicApi.tokens.columns.name")}</th>
              <th className="px-4 py-3 font-medium">{t("publicApi.tokens.columns.prefix")}</th>
              <th className="px-4 py-3 font-medium">{t("publicApi.tokens.columns.description")}</th>
              <th className="px-4 py-3 font-medium">{t("publicApi.tokens.columns.created")}</th>
              <th className="px-4 py-3 font-medium">{t("publicApi.tokens.columns.lastUsed")}</th>
              <th className="px-4 py-3 font-medium text-right">{t("publicApi.tokens.columns.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  {t("common.loading")}
                </td>
              </tr>
            )}
            {!isLoading && tokens.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  {t("publicApi.tokens.empty")}
                </td>
              </tr>
            )}
            {tokens.map((token) => (
              <tr key={token.id} className="border-t">
                <td className="px-4 py-3 font-medium">{token.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{token.tokenPrefix}…</td>
                <td className="px-4 py-3 text-muted-foreground">{token.description ?? "—"}</td>
                <td className="px-4 py-3 whitespace-nowrap">{formatDate(token.createdAt)}</td>
                <td className="px-4 py-3 whitespace-nowrap">{formatDate(token.lastUsedAt)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    {canUpdate && (
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label={t("publicApi.tokens.edit", "Edit token")}
                        onClick={() => setEditToken(token)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label={t("publicApi.tokens.revokeAria", "Revoke token")}
                        onClick={() => setRevokeToken(token)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateTokenDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        createdSecret={createdSecret}
        isPending={createMutation.isPending}
        onSubmit={(name, description) => createMutation.mutate({ name, description })}
        onCopy={copySecret}
      />

      {editToken && (
        <EditTokenDialog
          token={editToken}
          open={!!editToken}
          onOpenChange={(open) => { if (!open) setEditToken(null); }}
          isPending={updateMutation.isPending}
          onSubmit={(name, description) =>
            updateMutation.mutate({ id: editToken.id, name, description })
          }
        />
      )}

      {revokeToken && (
        <RevokeTokenDialog
          token={revokeToken}
          open={!!revokeToken}
          onOpenChange={(open) => { if (!open) setRevokeToken(null); }}
          isPending={revokeMutation.isPending}
          onConfirm={() => revokeMutation.mutate(revokeToken.id)}
        />
      )}
    </div>
  );
}

function CreateTokenDialog({
  open,
  onOpenChange,
  createdSecret,
  isPending,
  onSubmit,
  onCopy,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  createdSecret: string | null;
  isPending: boolean;
  onSubmit: (name: string, description: string) => void;
  onCopy: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setName("");
      setDescription("");
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {createdSecret ? t("publicApi.tokens.secretTitle") : t("publicApi.tokens.createTitle")}
          </DialogTitle>
        </DialogHeader>

        {createdSecret ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("publicApi.tokens.secretHint")}</p>
            <div className="flex gap-2">
              <Input readOnly value={createdSecret} className="font-mono text-xs" />
              <Button
                type="button"
                variant="outline"
                aria-label={t("publicApi.tokens.copy", "Copy token")}
                onClick={() => onCopy(createdSecret)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit(name.trim(), description.trim());
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="token-name">{t("publicApi.tokens.fields.name")}</Label>
              <Input
                id="token-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="token-description">{t("publicApi.tokens.fields.description")}</Label>
              <Textarea
                id="token-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={!name.trim() || isPending}>
                <KeyRound className="mr-2 h-4 w-4" />
                {t("publicApi.tokens.create")}
              </Button>
            </DialogFooter>
          </form>
        )}

        {createdSecret && (
          <DialogFooter>
            <Button onClick={() => handleOpenChange(false)}>{t("common.close")}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditTokenDialog({
  token,
  open,
  onOpenChange,
  isPending,
  onSubmit,
}: {
  token: PublicApiTokenListItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  onSubmit: (name: string, description: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(token.name);
  const [description, setDescription] = useState(token.description ?? "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("publicApi.tokens.editTitle")}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(name.trim(), description.trim());
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="edit-token-name">{t("publicApi.tokens.fields.name")}</Label>
            <Input
              id="edit-token-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-token-description">{t("publicApi.tokens.fields.description")}</Label>
            <Textarea
              id="edit-token-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={!name.trim() || isPending}>
              {t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RevokeTokenDialog({
  token,
  open,
  onOpenChange,
  isPending,
  onConfirm,
}: {
  token: PublicApiTokenListItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("publicApi.tokens.revokeTitle")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t("publicApi.tokens.revokeBody", { name: token.name })}
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="destructive" disabled={isPending} onClick={onConfirm}>
            {t("publicApi.tokens.revoke")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
